import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import { ILookupItemModel, ILookupItemSimpleModel } from "../types/types";
import React from "react";
import throttle from "lodash/throttle";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Delete";
interface ISingleValueAutoCompleteCategoryProps {
  options: ILookupItemModel[];
  onChange: (
    event: React.ChangeEvent<{}>,
    value: ILookupItemModel | null,
  ) => void;
  helperText: any;
  error: any;
  value: ILookupItemModel | null;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  freeSolo?: boolean;
  id: string;
}

export default function SingleValueAutoCompleteCategory(
  props: ISingleValueAutoCompleteCategoryProps,
) {
  const {
    options,
    onChange,
    value,
    label,
    placeholder = "Type or select", // Set default value
    readOnly = false,
    freeSolo = true,
    id,
  } = props;
  // The live theme, so the "add new" hint follows affiliate branding.
  const brand = useTheme();
  const [inputValue, setInputValue] = React.useState("");

  const getOptionsWithAddNew = React.useMemo(() => {
    if (!inputValue.trim() || inputValue.length < 2) {
      return options;
    }

    const searchTerm = inputValue.trim().toLowerCase();
    const hasExactMatch = options.some(
      (option) => option.name?.toLowerCase() === searchTerm,
    );

    if (!hasExactMatch && inputValue.trim().length > 0) {
      // Add "Add [name]" option at the beginning
      const addNewOption: ILookupItemSimpleModel & { isAddNew?: boolean } = {
        id: -1, // Special ID to identify this as "add new" option
        name: `Add "${inputValue.trim()}"`,
        isAddNew: true,
      };
      return [addNewOption, ...options];
    }

    return options;
  }, [options, inputValue]);
  return (
    <Grid container direction={"column"} gap={1}>
      <Autocomplete
        id={id}
        ChipProps={{ variant: "outlined", color: "secondary" }}
        openOnFocus
        readOnly={readOnly}
        freeSolo={freeSolo}
        disablePortal
        options={getOptionsWithAddNew}
        value={value}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, newValue) => {
          // Handle the "Add xxx" option
          if (newValue && (newValue as any).isAddNew) {
            // Create a new option from the input value
            const newOption = {
              name: inputValue.trim(),
              id: Math.floor(Math.random() * -1e12), // Generate a temporary negative ID
            } as ILookupItemModel;
            onChange(event, newOption);
            return;
          }

          if (typeof newValue === "string") {
            onChange(event, {
              name: newValue,
              id: Math.floor(Math.random() * -1e12),
            } as ILookupItemModel);
          } else {
            onChange(event, newValue);
          }
        }}
        onBlur={(e) => {
          const trimmed = inputValue.trim();
          if (freeSolo && trimmed && (value?.name ?? "") !== trimmed) {
            onChange(
              e as any,
              {
                name: trimmed,
                id: Math.floor(Math.random() * -1e12),
              } as ILookupItemModel,
            );
          }
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          if (option.name) {
            return option.name;
          }
          return option.name!;
        }}
        renderOption={(props, option) => {
          const isAddNew = (option as any).isAddNew;
          return (
            <li
              {...props}
              key={option.id}
              style={{
                ...props.style,
                fontWeight: isAddNew ? "bold" : "normal",
                color: isAddNew ? brand.palette.primary.dark : "inherit",
                fontStyle: isAddNew ? "italic" : "normal",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <span>{option.name}</span>
              </Stack>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...params.inputProps, maxLength: 50 }}
            label={label}
            placeholder={placeholder}
          />
        )}
      />
    </Grid>
  );
}
